import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Box, Card, CardContent, Grid, Typography, TextField, InputAdornment,
  Tabs, Tab, Chip, Stack, Table, TableHead, TableBody, TableRow, TableCell,
  TableContainer, Paper, CircularProgress, Alert, IconButton, Tooltip,
} from '@mui/material';
import { Search, Refresh, Forum, Schedule, ErrorOutline, WhatsApp } from '@mui/icons-material';
import { AdminLayout } from '../../components/Layout/AdminLayout';
import { PageHeader } from '../../components/common';
import { whatsAppService, WhatsAppMessage } from '../../services/whatsapp.service';
import { colors } from '../../theme/theme';

const fmtTime = (s: string | null | undefined) =>
  s ? new Date(s).toLocaleString() : '—';

const summarisePayload = (m: WhatsAppMessage): string => {
  const p = m.payload || {};
  if (p.text?.body) return p.text.body;
  if (p.interactive?.body?.text) return `[list] ${p.interactive.body.text.slice(0, 60)}`;
  if (p.interactive?.button_reply?.title) return `tapped: ${p.interactive.button_reply.title}`;
  if (p.interactive?.list_reply?.title) return `picked: ${p.interactive.list_reply.title}`;
  if (m.type === 'status') return `status → ${m.status}`;
  return `[${m.type ?? 'unknown'}]`;
};

export const WhatsAppOpsPage = () => {
  const [tab, setTab] = useState(0);
  const [phoneLookup, setPhoneLookup] = useState('');

  const statsQuery = useQuery({
    queryKey: ['wa', 'stats'],
    queryFn: () => whatsAppService.stats(),
    refetchInterval: 30_000,
  });
  const stats = (statsQuery.data as any)?.data;

  const stuckQuery = useQuery({
    queryKey: ['wa', 'stuck'],
    queryFn: () => whatsAppService.stuckSessions(60),
    refetchInterval: 60_000,
  });
  const stuck = (stuckQuery.data as any)?.data ?? [];

  const conversationQuery = useQuery({
    queryKey: ['wa', 'conversation', phoneLookup],
    queryFn: () => whatsAppService.conversation(phoneLookup),
    enabled: !!phoneLookup && phoneLookup.length >= 8,
  });
  const conv = (conversationQuery.data as any)?.data;

  return (
    <AdminLayout>
      <PageHeader
        title="WhatsApp Ops"
        subtitle="Diagnose conversations, surface stuck sessions, monitor channel health"
        breadcrumbs={[
          { label: 'Dashboard', path: '/superadmin/dashboard' },
          { label: 'WhatsApp Ops' },
        ]}
      />

      {/* KPI cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ background: `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.primary.dark} 100%)`, color: 'white' }}>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.85)', textTransform: 'uppercase', fontWeight: 600 }}>
                    Active sessions
                  </Typography>
                  <Typography variant="h4" fontWeight={700} sx={{ color: '#fff', mt: 0.5 }}>
                    {stats?.sessions_active ?? '—'}
                  </Typography>
                </Box>
                <Forum sx={{ fontSize: 32, color: 'rgba(255,255,255,0.4)' }} />
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ background: `linear-gradient(135deg, ${colors.success.main} 0%, ${colors.success.dark} 100%)`, color: 'white' }}>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.85)', textTransform: 'uppercase', fontWeight: 600 }}>
                    Inbound today
                  </Typography>
                  <Typography variant="h4" fontWeight={700} sx={{ color: '#fff', mt: 0.5 }}>
                    {stats?.messages_today_inbound ?? '—'}
                  </Typography>
                </Box>
                <WhatsApp sx={{ fontSize: 32, color: 'rgba(255,255,255,0.4)' }} />
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ background: `linear-gradient(135deg, ${colors.info.main} 0%, ${colors.info.dark} 100%)`, color: 'white' }}>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.85)', textTransform: 'uppercase', fontWeight: 600 }}>
                    Outbound today
                  </Typography>
                  <Typography variant="h4" fontWeight={700} sx={{ color: '#fff', mt: 0.5 }}>
                    {stats?.messages_today_outbound ?? '—'}
                  </Typography>
                </Box>
                <WhatsApp sx={{ fontSize: 32, color: 'rgba(255,255,255,0.4)' }} />
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ background: `linear-gradient(135deg, ${colors.error.main} 0%, ${colors.error.dark} 100%)`, color: 'white' }}>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.85)', textTransform: 'uppercase', fontWeight: 600 }}>
                    Failed today
                  </Typography>
                  <Typography variant="h4" fontWeight={700} sx={{ color: '#fff', mt: 0.5 }}>
                    {stats?.messages_failed_today ?? '—'}
                  </Typography>
                </Box>
                <ErrorOutline sx={{ fontSize: 32, color: 'rgba(255,255,255,0.4)' }} />
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
          <Tab label="Conversation lookup" />
          <Tab label={`Stuck sessions${stuck.length ? ` (${stuck.length})` : ''}`} />
        </Tabs>
        <CardContent>
          {tab === 0 && (
            <>
              <TextField
                fullWidth size="small" placeholder="Enter a citizen's WhatsApp number (e.g. 254700000000)"
                value={phoneLookup}
                onChange={(e) => setPhoneLookup(e.target.value.trim())}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search fontSize="small" sx={{ color: colors.grey[500] }} />
                    </InputAdornment>
                  ),
                }}
                helperText="Type at least 8 digits to look up — without country code prefix it falls back to whatever you've stored"
                sx={{ mb: 2 }}
              />

              {conversationQuery.isLoading && (
                <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
              )}

              {conv && (
                <>
                  {conv.session ? (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      Current state: <strong>{conv.session.state}</strong>
                      {conv.session.county?.name && <> • County: <strong>{conv.session.county.name}</strong></>}
                      {conv.session.user && <> • User: <strong>{conv.session.user.first_name} {conv.session.user.last_name}</strong></>}
                      {' '}• Last update: {fmtTime(conv.session.updated_at)}
                    </Alert>
                  ) : (
                    <Alert severity="warning" sx={{ mb: 2 }}>No active session for this number.</Alert>
                  )}

                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ width: 160 }}>When</TableCell>
                          <TableCell sx={{ width: 100 }}>Direction</TableCell>
                          <TableCell sx={{ width: 110 }}>Type</TableCell>
                          <TableCell>Summary</TableCell>
                          <TableCell sx={{ width: 100 }}>Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {conv.messages.length === 0 ? (
                          <TableRow><TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>No messages yet for this number.</TableCell></TableRow>
                        ) : conv.messages.slice().reverse().map((m: WhatsAppMessage) => (
                          <TableRow key={m.id} hover>
                            <TableCell>{fmtTime(m.created_at)}</TableCell>
                            <TableCell>
                              <Chip size="small" label={m.direction}
                                color={m.direction === 'inbound' ? 'primary' : 'success'} />
                            </TableCell>
                            <TableCell>{m.type || '—'}</TableCell>
                            <TableCell sx={{ maxWidth: 380, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {summarisePayload(m)}
                            </TableCell>
                            <TableCell>{m.status || '—'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}
            </>
          )}

          {tab === 1 && (
            <>
              <Stack direction="row" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Sessions in a non-idle state for more than 1 hour with no recent activity.
                </Typography>
                <Box flex={1} />
                <Tooltip title="Refresh">
                  <IconButton onClick={() => stuckQuery.refetch()} disabled={stuckQuery.isFetching}>
                    <Refresh />
                  </IconButton>
                </Tooltip>
              </Stack>

              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Phone</TableCell>
                      <TableCell>County</TableCell>
                      <TableCell>User</TableCell>
                      <TableCell>Stuck at</TableCell>
                      <TableCell>Since</TableCell>
                      <TableCell sx={{ width: 90 }}></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {stuck.length === 0 ? (
                      <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>No stuck sessions. <Schedule sx={{ fontSize: 16, ml: 0.5, verticalAlign: 'middle' }} /></TableCell></TableRow>
                    ) : stuck.map((s: any) => (
                      <TableRow key={s.id} hover>
                        <TableCell>{s.wa_phone}</TableCell>
                        <TableCell>{s.county?.name || '—'}</TableCell>
                        <TableCell>{s.user ? `${s.user.first_name ?? ''} ${s.user.last_name ?? ''}`.trim() : 'anonymous'}</TableCell>
                        <TableCell><Chip size="small" label={s.state} /></TableCell>
                        <TableCell>{fmtTime(s.updated_at)}</TableCell>
                        <TableCell>
                          <Tooltip title="View conversation">
                            <IconButton size="small" onClick={() => { setPhoneLookup(s.wa_phone); setTab(0); }}>
                              <Forum fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </CardContent>
      </Card>
    </AdminLayout>
  );
};
